// Navbar.js - Responsive navigation bar with scroll effects and mobile menu functionality

import React, { useState, useEffect } from 'react';
import {
    MessageCircle,
    Menu,
    X
} from 'lucide-react';
import { getWhatsAppUrl } from '../config/constants';
import { Link, useLocation } from 'react-router-dom';

const Navbar = ({ onShowAds }) => {
    // Track scroll position and mobile menu state
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('inicio');

    // Get current location for route detection
    const location = useLocation();

    // Navigation items for both desktop and mobile views
    const navItems = [
        { name: 'Inicio', id: 'inicio' },
        { name: 'Nosotros', id: 'nosotros' },
        { name: 'Servicios', id: 'servicios' },
        { name: 'Productos', id: 'productos' },
        { name: 'LogoTool', id: 'logotool' }
    ];

    // Handle scroll events for sticky navigation and section detection
    const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);

        // Detect which section is currently in view
        const sections = navItems.map(item => item.id);

        for (const section of sections) {
            const element = document.getElementById(section);
            if (element) {
                const rect = element.getBoundingClientRect();
                // If the section is in the viewport (with some offset for better UX)
                if (rect.top <= 150 && rect.bottom >= 150) {
                    setActiveSection(section);
                    break;
                }
            }
        }
    };

    // Clean up scroll event listener on component unmount
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        // Initial check
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Update active section based on route change
    useEffect(() => {
        const path = location.pathname.replace('/', '') || 'inicio';
        setActiveSection(path);
    }, [location]);

    // Check if current route or active section is servicios or productos
    const isServiciosOrProductos = () => {
        return activeSection === 'servicios' || activeSection === 'productos';
    };

    // Get logo source based on active section
    const getLogoSrc = () => {
        return isServiciosOrProductos()
            ? require('../assets/logo-b.png')  // Black logo for servicios/productos
            : require('../assets/logo-w.png'); // White logo for all others
    };

    // Get text color class based on active section
    const getTextColorClass = () => {
        return isServiciosOrProductos() ? 'text-black' : 'text-white';
    };

    // Get hover color class based on active section
    const getHoverColorClass = () => {
        return isServiciosOrProductos() ? 'hover:text-gray-600' : 'hover:text-primary';
    };

    // Get active item style
    const getActiveItemClass = (itemId) => {
        const baseClass = getTextColorClass();
        const activeClass = activeSection === itemId ? 'font-bold border-b-2 border-accent' : '';
        return `${baseClass} ${activeClass}`;
    };

    // Extract WhatsApp URL from constants
    const whatsappUrl = getWhatsAppUrl();

    // Determine if rounded effect should be applied to navigation container
    const shouldShowRoundedEffect = isScrolled && !isMobileMenuOpen;

    // Render desktop menu items with hover effects
    const renderDesktopNavItems = () => (
        navItems.map((item) => (
            <Link
                key={item.id}
                to={`/${item.id === 'inicio' ? 'inicio' : item.id}`}
                className={`${getActiveItemClass(item.id)} ${getHoverColorClass()} smooth-transition px-3 py-2 text-sm font-medium relative group drop-shadow-xl`}
                onClick={() => setActiveSection(item.id)}
            >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent group-hover:w-full smooth-transition"></span>
            </Link>
        ))
    );

    // Render mobile menu items with click handlers
    const renderMobileNavItems = () => (
        navItems.map((item) => (
            <Link
                key={item.id}
                to={`/${item.id === 'inicio' ? 'inicio' : item.id}`}
                className={`${getActiveItemClass(item.id)} ${getHoverColorClass()} smooth-transition py-3 px-4 rounded-lg hover:bg-white/10 text-lg font-medium`}
                onClick={() => {
                    setActiveSection(item.id);
                    setIsMobileMenuOpen(false);
                }}
            >
                {item.name}
            </Link>
        ))
    );

    // Render WhatsApp button for desktop view
    const renderWhatsAppButtonDesktop = () => (
        <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full flex items-center space-x-2 smooth-transition hover-glow shadow-lg"
        >
            <MessageCircle size={20} />
            <span>WhatsApp</span>
        </a>
    );

    // Render Ofertas button for desktop view
    const renderOfertasButtonDesktop = () => (
        <button
            onClick={onShowAds}
            className={`${getTextColorClass()} ${getHoverColorClass()} smooth-transition px-3 py-2 text-sm font-medium relative group drop-shadow-xl`}
        >
            Ofertas 🔥
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent group-hover:w-full smooth-transition"></span>
        </button>
    );

    // Render mobile menu toggle button
    const renderMobileMenuToggle = () => (
        <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`${getTextColorClass()} hover:text-primary p-2 rounded-lg glass-effect`}
        >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
    );

    // Render WhatsApp button for mobile view
    const renderWhatsAppButtonMobile = () => (
        <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-full flex items-center justify-center space-x-2 smooth-transition mt-4"
            onClick={() => setIsMobileMenuOpen(false)}
        >
            <MessageCircle size={20} />
            <span>Escríbenos por WhatsApp</span>
        </a>
    );

    // Render Ofertas button for mobile view
    const renderOfertasButtonMobile = () => (
        <button
            onClick={() => {
                onShowAds();
                setIsMobileMenuOpen(false);
            }}
            className={`${getTextColorClass()} ${getHoverColorClass()} smooth-transition py-3 px-4 rounded-lg hover:bg-white/10 text-lg font-medium text-left`}
        >
            Ofertas 🔥
        </button>
    );

    // Render navigation container with appropriate styling based on scroll state
    const renderNavigationContainer = () => (
        <div className={`duration-300 ease-linear ${shouldShowRoundedEffect
            ? 'mx-auto mt-4 rounded-full glass-effect bg-black/20 backdrop-blur-lg max-w-7xl border border-white/10'
            : 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-4 rounded-full bg-transparent border border-transparent'}`}>
            <div className={`${shouldShowRoundedEffect ? 'px-4 sm:px-6 lg:px-8' : ''}`}>
                <div className="flex items-center justify-between h-20">
                    {/* Logo section with dynamic logo */}
                    <div className="flex-shrink-0">
                        <Link to={`home`} className="flex items-center">
                            <img
                                src={getLogoSrc()}
                                alt="Print Studio"
                                className="w-auto smooth-transition hover:scale-150 drop-shadow-xl"
                                style={{ width: '60px', height: '60px' }}
                            />
                        </Link>
                    </div>

                    {/* Desktop menu items */}
                    <div className="hidden md:block">
                        <div className="ml-10 flex items-baseline space-x-8 drop-shadow-xl whitespace-nowrap">
                            {renderDesktopNavItems()}
                            {renderOfertasButtonDesktop()}
                        </div>
                    </div>

                    {/* WhatsApp button for desktop */}
                    <div className="hidden min-[950px]:block">
                        {renderWhatsAppButtonDesktop()}
                    </div>

                    {/* Mobile menu toggle button */}
                    <div className="md:hidden">
                        {renderMobileMenuToggle()}
                    </div>
                </div>

                {/* Mobile menu content when open */}
                {isMobileMenuOpen && (
                    <div className={`md:hidden glass-effect rounded-lg mt-2 p-6 backdrop-blur-xl border border-white/10`}>
                        <div className="flex flex-col space-y-4">
                            {renderMobileNavItems()}
                            {renderOfertasButtonMobile()}
                            {renderWhatsAppButtonMobile()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <nav className={`fixed top-0 w-full z-50 smooth-transition ${isScrolled ? '' : 'bg-transparent'}`}>
            {renderNavigationContainer()}
        </nav>
    );
};

export default Navbar;